import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { IPublicClientApplication, PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { MsalGuard, MsalInterceptor, MsalBroadcastService, MsalInterceptorConfiguration, MsalModule, MsalService, MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalGuardConfiguration, ProtectedResourceScopes } from '@azure/msal-angular';
import { msalConfig, loginRequest, protectedResources } from './auth-config';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { LoggedoutModule } from './loggedout/loggedout.module';
import { SharedModule } from './shared/shared.module';
import { DashboardModule } from './dashboard/dashboard.module';
import { AdminModule } from './admin/admin.module';
import { AppRoutingModule } from './app-routing.module';
import { CompanyModule } from './company/company.module';
import { AccountModule } from './account/account.module';
import { AuditModule } from './audit/audit.module';
import { CompanyAuditModule } from './company-audit/company-audit.module';
import { QuestionModule } from './question/question.module';
import { ModuleModule } from './module/module.module';
import { AuditTemplateModule } from './audit-template/audit-template.module';
import { LendersModule } from './lenders/lenders.module';
import { BrokersModule } from './brokers/brokers.module';
import { HTTP_INTERCEPTORS } from '@angular/common/http';



/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
    return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string | ProtectedResourceScopes> | null>();

    //comment this section out if you don't want to protect the dotnet API
    protectedResourceMap.set(protectedResources.dotNetApi.endpoint, [
        {
            httpMethod: 'GET',
            scopes: [...protectedResources.dotNetApi.scopes.read]
        },
        {
            httpMethod: 'POST',
            scopes: [...protectedResources.dotNetApi.scopes.write]
        },
        {
            httpMethod: 'PUT',
            scopes: [...protectedResources.dotNetApi.scopes.write]
        },
        {
            httpMethod: 'DELETE',
            scopes: [...protectedResources.dotNetApi.scopes.write]
        }

    ]
    );
     //comment the section out above - and uncomment this below if you want to take away protection
    //protectedResourceMap.set("api/*",null);

    return {
        interactionType: InteractionType.Popup,
        protectedResourceMap,
    };
}

/**
* Set your default interaction type for MSALGuard here. If you have any
* additional scopes you want the user to consent upon login, add them here as well.
*/
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: loginRequest
    };
}


@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        MsalModule,
        SharedModule,
        AuthModule,
        DashboardModule,
        AdminModule,
        AccountModule,
        LoggedoutModule,
        CompanyModule,
        AuditModule,
        CompanyAuditModule,
        AuditTemplateModule,
        QuestionModule,
        ModuleModule,
        LendersModule,
        BrokersModule,
        BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
        AppRoutingModule
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
